import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { auth, db } from "../Config/firebase";
import { aplicarTenantAtual, consultaTenantAtual } from "../Utils/tenant";
import RelatorioPlanoComunicacaoPrint from "./RelatorioPlanoComunicacaoPrint";
import { AMBIENTE_PRODUCAO, NOME_CLIENTE_DOCUMENTO } from "../Config/branding";

const COLLECTION = "tblPlanosComunicacao";
const OBJETIVO_PADRAO = `O presente documento tem como objetivo estabelecer o Plano de Comunicação da ${NOME_CLIENTE_DOCUMENTO}, em conformidade com os requisitos da ABNT NBR ISO 9001:2015, bem como com a Política da Qualidade e os procedimentos internos do Sistema de Gestão da Qualidade (SGQ).\n\nA comunicação eficaz é fundamental para garantir a transparência, a confiabilidade das informações e o alinhamento entre colaboradores, direção, clientes e demais partes interessadas. Este plano define o que comunicar, a quem comunicar, como comunicar e com que frequência, assegurando que todas as informações relevantes para o desempenho do SGQ sejam transmitidas de forma clara, tempestiva e documentada.\n\nDessa forma, este documento contribui para promover o entendimento e engajamento dos colaboradores em relação aos objetivos e à Política da Qualidade; garantir a rastreabilidade e a confiabilidade das informações técnicas e gerenciais; apoiar a tomada de decisão baseada em evidências; e favorecer a melhoria contínua e a satisfação dos clientes, em alinhamento com a missão, visão e valores da organização.\n\nAssim, o Plano de Comunicação consolida-se como uma ferramenta estratégica para assegurar a eficácia do Sistema de Gestão da Qualidade e o alcance dos resultados planejados.`;

const COMUNICACOES_PADRAO = [
  ["Política da Qualidade", "Garantir entendimento e alinhamento estratégico", "Todos os colaboradores, clientes e partes interessadas", "Placa de entrada, murais internos, site, e-mails e reuniões", "Permanente e em treinamentos de integração"],
  ["Objetivos da Qualidade", "Acompanhar desempenho e metas", "Direção, SGQ e setores operacionais", "Reuniões de análise crítica e relatórios", "Anual (ou trimestral se necessário)"],
  ["Resultados de Ensaios e Laudos", "Entregar informações técnicas confiáveis", "Clientes e contratantes", "Laudos entregues pela Direção", "Conforme conclusão de ensaio"],
  ["Não Conformidades e Melhorias", "Garantir tratamento eficaz e prevenção de recorrência", "Colaboradores, SGQ e Direção", "Formulário eletrônico e reuniões", "Sempre que ocorrer"],
  ["Auditorias Internas e Externas", "Avaliar conformidade do SGQ", "Equipe auditada, direção e auditores", "Reuniões e relatórios de auditoria", "Conforme cronograma (mínimo anual)"],
  ["Análise Crítica pela Direção", "Avaliar desempenho global do SGQ", "Direção e SGQ", "Reunião formal com atas", "Anual"],
  ["Treinamentos e Capacitações", "Desenvolver competência técnica e de SGQ", "Colaboradores", "Treinamentos presenciais, manuais e POPs", "Conforme necessidade (mínimo anual)"],
  ["Indicadores de Desempenho (KPIs)", "Monitorar eficácia dos processos", "Direção e líderes de setor", "Painéis e relatórios mensais", "Mensal"],
  ["Satisfação do Cliente", "Avaliar percepção e oportunidades de melhoria", "Clientes", "Pesquisa de satisfação online", "Após entrega de serviços"],
  ["Satisfação do Funcionário", "Avaliar percepção e oportunidades de melhoria", "Colaboradores", "Pesquisa de satisfação online", "Sempre que necessário"],
  ["Comunicação de Emergência / Crítica", "Garantir resposta rápida e eficaz", "Colaboradores e clientes afetados", "WhatsApp corporativo, telefone e e-mail", "Sempre que necessário"],
  ["Acidente do Trabalho", "Comunicar ao INSS em caso de acidente do trabalho", "Empregados da instituição", "Formulário eletrônico (Google Forms): Abertura de CAT - Comunicação de Acidente do Trabalho", "Quando houver acidente do trabalho"],
  ["Quebra ou descarte de equipamento", "Evidenciar o descarte de equipamento", "Direção", "Sistema web", "Sempre que houver"],
].map(([oQueComunicar, objetivo, publicoAlvo, meioComunicacao, frequencia]) => ({
  oQueComunicar,
  objetivo,
  publicoAlvo,
  meioComunicacao,
  frequencia,
}));

function criarPlanoInicial() {
  return {
    versao: AMBIENTE_PRODUCAO ? "01" : "",
    aprovadoEm: AMBIENTE_PRODUCAO ? "2026-09-01" : "",
    objetivoGeral: OBJETIVO_PADRAO,
    comunicacoes: COMUNICACOES_PADRAO.map((item) => ({ ...item })),
  };
}

function formatarData(data) {
  if (!data) return "-";
  const [ano, mes, dia] = String(data).split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : data;
}

function PlanoComunicacaoVisual({ item, onEditar, onAdicionarItem, onRelatorio }) {
  const comunicacoes = Array.isArray(item.comunicacoes) ? item.comunicacoes : [];

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="body2" fontWeight={700}>
          Versão {item.versao || "-"} — Aprovado em {formatarData(item.aprovadoEm)}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => onEditar(item)}>
            Editar plano
          </Button>
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => onAdicionarItem(item)}>
            Novo item
          </Button>
          <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => onRelatorio(item)}>
            Relatório
          </Button>
          <HistoricoCadastro colecao={COLLECTION} documentoId={item.id} registro={item} />
        </Stack>
      </Stack>

        <Box sx={{ mb: 2, whiteSpace: "pre-line" }}>
          <Typography variant="body2"><strong>Objetivo:</strong> {item.objetivoGeral || "-"}</Typography>
        </Box>

      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 1050, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              {[
                ["O que comunicar", "#063b70", "20%"],
                ["Objetivo", "#063b70", "21%"],
                ["Público-Alvo", "#063b70", "20%"],
                ["Meio de Comunicação", "#258b86", "23%"],
                ["Frequência", "#063b70", "16%"],
              ].map(([titulo, cor, largura]) => (
                <TableCell key={titulo} align="center" sx={{ width: largura, backgroundColor: cor, color: "white", border: "1px solid #94a3b8", fontWeight: 800, py: 1.5 }}>
                  {titulo}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {comunicacoes.map((comunicacao, indice) => (
              <TableRow key={`${comunicacao.oQueComunicar}-${indice}`}>
                <TableCell sx={{ border: "1px solid #94a3b8", fontWeight: 700, whiteSpace: "pre-line" }}>{comunicacao.oQueComunicar || "-"}</TableCell>
                <TableCell align="center" sx={{ border: "1px solid #94a3b8", whiteSpace: "pre-line" }}>{comunicacao.objetivo || "-"}</TableCell>
                <TableCell align="center" sx={{ border: "1px solid #94a3b8", whiteSpace: "pre-line" }}>{comunicacao.publicoAlvo || "-"}</TableCell>
                <TableCell align="center" sx={{ border: "1px solid #94a3b8", whiteSpace: "pre-line" }}>{comunicacao.meioComunicacao || "-"}</TableCell>
                <TableCell align="center" sx={{ border: "1px solid #94a3b8", whiteSpace: "pre-line" }}>{comunicacao.frequencia || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

export default function CadastroPlanoComunicacao() {
  const [planos, setPlanos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [planoEditando, setPlanoEditando] = useState(null);
  const [planoRelatorio, setPlanoRelatorio] = useState(null);
  const [plano, setPlano] = useState(criarPlanoInicial());
  const [notificacao, setNotificacao] = useState({ aberta: false, mensagem: "", tipo: "success" });
  const cadastrandoPadraoRef = useRef(false);

  const planosExibidos = useMemo(() => {
    const chaves = new Set();
    return planos.filter((item) => {
      const chave = `${item.versao || ""}|${item.aprovadoEm || ""}|${item.objetivoGeral || ""}`;
      if (chaves.has(chave)) return false;
      chaves.add(chave);
      return true;
    });
  }, [planos]);

  async function carregarPlanos() {
    setCarregando(true);
    try {
      const snapshot = await getDocs(consultaTenantAtual(query(collection(db, COLLECTION), orderBy("criadoEm", "desc"))));
      setPlanos(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch (error) {
      console.error("Erro ao carregar planos de comunicação:", error);
      setNotificacao({ aberta: true, mensagem: "Não foi possível carregar os planos de comunicação.", tipo: "error" });
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregarPlanos();
  }, []);

  async function cadastrarPlanoPadrao() {
    if (planos.length > 0 || cadastrandoPadraoRef.current) return;

    cadastrandoPadraoRef.current = true;
    const planoInicial = criarPlanoInicial();
    setSalvando(true);
    try {
      const documentoCriado = await addDoc(collection(db, COLLECTION), aplicarTenantAtual({
        ...planoInicial,
        criadoEm: serverTimestamp(),
        criadoPor: auth.currentUser?.uid || null,
        atualizadoEm: serverTimestamp(),
        atualizadoPor: auth.currentUser?.uid || null,
      }));
      setPlanos([{ id: documentoCriado.id, ...planoInicial }]);
      setNotificacao({ aberta: true, mensagem: "Plano padrão versão 01 cadastrado com sucesso.", tipo: "success" });
    } catch (error) {
      console.error("Erro ao cadastrar plano padrão de comunicação:", error);
      setNotificacao({ aberta: true, mensagem: "Não foi possível cadastrar o plano padrão.", tipo: "error" });
    } finally {
      cadastrandoPadraoRef.current = false;
      setSalvando(false);
    }
  }

  function abrirEdicao(item) {
    setPlanoEditando(item);
    setPlano({
      versao: item.versao || "",
      aprovadoEm: item.aprovadoEm || "",
      objetivoGeral: item.objetivoGeral || "",
      comunicacoes: Array.isArray(item.comunicacoes) ? item.comunicacoes.map((comunicacao) => ({ ...comunicacao })) : [],
    });
    setDialogAberto(true);
  }

  function abrirNovoItem(item) {
    setPlanoEditando(item);
    setPlano({
      versao: item.versao || "",
      aprovadoEm: item.aprovadoEm || "",
      objetivoGeral: item.objetivoGeral || "",
      comunicacoes: [
        ...(Array.isArray(item.comunicacoes) ? item.comunicacoes.map((comunicacao) => ({ ...comunicacao })) : []),
        { oQueComunicar: "", objetivo: "", publicoAlvo: "", meioComunicacao: "", frequencia: "" },
      ],
    });
    setDialogAberto(true);
  }

  function atualizarPlano(campo, valor) {
    setPlano((atual) => ({ ...atual, [campo]: valor }));
  }

  function atualizarComunicacao(indice, campo, valor) {
    setPlano((atual) => ({
      ...atual,
      comunicacoes: atual.comunicacoes.map((item, index) => index === indice ? { ...item, [campo]: valor } : item),
    }));
  }

  function adicionarComunicacao() {
    setPlano((atual) => ({
      ...atual,
      comunicacoes: [...atual.comunicacoes, { oQueComunicar: "", objetivo: "", publicoAlvo: "", meioComunicacao: "", frequencia: "" }],
    }));
  }

  function removerComunicacao(indice) {
    setPlano((atual) => ({ ...atual, comunicacoes: atual.comunicacoes.filter((_, index) => index !== indice) }));
  }

  async function salvar() {
    if (!plano.versao.trim() || !plano.objetivoGeral.trim()) {
      setNotificacao({ aberta: true, mensagem: "Informe a versão e o objetivo do plano.", tipo: "warning" });
      return;
    }

    if (plano.comunicacoes.some((item) => !item.oQueComunicar.trim())) {
      setNotificacao({ aberta: true, mensagem: "Informe ao menos o item " + "“O que comunicar” em cada linha.", tipo: "warning" });
      return;
    }

    const dados = {
      versao: plano.versao.trim(),
      aprovadoEm: plano.aprovadoEm,
      objetivoGeral: plano.objetivoGeral.trim(),
      comunicacoes: plano.comunicacoes.map((item) => ({
        oQueComunicar: item.oQueComunicar.trim(),
        objetivo: item.objetivo.trim(),
        publicoAlvo: item.publicoAlvo.trim(),
        meioComunicacao: item.meioComunicacao.trim(),
        frequencia: item.frequencia.trim(),
      })),
      atualizadoEm: serverTimestamp(),
      atualizadoPor: auth.currentUser?.uid || null,
    };

    setSalvando(true);
    try {
      if (planoEditando) {
        await updateDoc(doc(db, COLLECTION, planoEditando.id), dados);
      } else {
        await addDoc(collection(db, COLLECTION), aplicarTenantAtual({
          ...dados,
          criadoEm: serverTimestamp(),
          criadoPor: auth.currentUser?.uid || null,
        }));
      }
      setDialogAberto(false);
      setNotificacao({ aberta: true, mensagem: planoEditando ? "Plano de Comunicação atualizado com sucesso." : "Plano de Comunicação cadastrado com sucesso.", tipo: "success" });
      await carregarPlanos();
    } catch (error) {
      console.error("Erro ao salvar Plano de Comunicação:", error);
      setNotificacao({ aberta: true, mensagem: "Não foi possível salvar o Plano de Comunicação.", tipo: "error" });
    } finally {
      setSalvando(false);
    }
  }

  if (planoRelatorio) {
    return <RelatorioPlanoComunicacaoPrint plano={planoRelatorio} onVoltar={() => setPlanoRelatorio(null)} />;
  }

  return (
    <>
      <Navbar />
      <Box sx={{ p: { xs: 2, md: 4 }, mt: 6 }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0} sx={{ mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} alignItems={{ md: "center" }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>RQ 1.14 — Plano de Comunicação</Typography>
              <Typography variant="body2" color="text.secondary">Planejamento das comunicações do Sistema de Gestão da Qualidade.</Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              {planos.length === 0 && !carregando && <Button variant="outlined" onClick={cadastrarPlanoPadrao} disabled={salvando}>Cadastrar plano padrão</Button>}
            </Stack>
          </Stack>
          {carregando && <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>}
          {!carregando && planos.length === 0 && <Box sx={{ p: 4, textAlign: "center" }}>Nenhum Plano de Comunicação cadastrado.</Box>}
        {!carregando && planosExibidos.map((item) => <PlanoComunicacaoVisual key={item.id} item={item} onEditar={abrirEdicao} onAdicionarItem={abrirNovoItem} onRelatorio={setPlanoRelatorio} />)}
        </Paper>
      </Box>

      <Dialog open={dialogAberto} onClose={() => !salvando && setDialogAberto(false)} fullWidth maxWidth="xl">
        <DialogTitle>{planoEditando ? "Editar Plano de Comunicação" : "Novo Plano de Comunicação"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label="Versão" value={plano.versao} onChange={(event) => atualizarPlano("versao", event.target.value)} sx={{ maxWidth: { sm: 180 } }} fullWidth required />
              <TextField label="Aprovado em" type="date" value={plano.aprovadoEm} onChange={(event) => atualizarPlano("aprovadoEm", event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            </Stack>
            <TextField label="Objetivo" value={plano.objetivoGeral} onChange={(event) => atualizarPlano("objetivoGeral", event.target.value)} multiline minRows={4} fullWidth required />
            <Typography variant="h6" fontWeight={700}>Matriz de comunicação</Typography>
            <Paper variant="outlined" sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 1200 }}>
                <TableHead><TableRow><TableCell>O que comunicar</TableCell><TableCell>Objetivo</TableCell><TableCell>Público-alvo</TableCell><TableCell>Meio de comunicação</TableCell><TableCell>Frequência</TableCell><TableCell /></TableRow></TableHead>
                <TableBody>{plano.comunicacoes.map((item, indice) => <TableRow key={indice}>
                  <TableCell sx={{ minWidth: 200 }}><TextField value={item.oQueComunicar} onChange={(event) => atualizarComunicacao(indice, "oQueComunicar", event.target.value)} size="small" fullWidth required /></TableCell>
                  <TableCell sx={{ minWidth: 220 }}><TextField value={item.objetivo} onChange={(event) => atualizarComunicacao(indice, "objetivo", event.target.value)} size="small" multiline minRows={2} fullWidth /></TableCell>
                  <TableCell sx={{ minWidth: 200 }}><TextField value={item.publicoAlvo} onChange={(event) => atualizarComunicacao(indice, "publicoAlvo", event.target.value)} size="small" multiline minRows={2} fullWidth /></TableCell>
                  <TableCell sx={{ minWidth: 220 }}><TextField value={item.meioComunicacao} onChange={(event) => atualizarComunicacao(indice, "meioComunicacao", event.target.value)} size="small" multiline minRows={2} fullWidth /></TableCell>
                  <TableCell sx={{ minWidth: 180 }}><TextField value={item.frequencia} onChange={(event) => atualizarComunicacao(indice, "frequencia", event.target.value)} size="small" multiline minRows={2} fullWidth /></TableCell>
                  <TableCell><Tooltip title="Remover linha"><IconButton size="small" color="error" onClick={() => removerComunicacao(indice)}><DeleteOutlineIcon /></IconButton></Tooltip></TableCell>
                </TableRow>)}</TableBody>
              </Table>
            </Paper>
            <Button startIcon={<AddIcon />} onClick={adicionarComunicacao} sx={{ alignSelf: "flex-start" }}>Adicionar comunicação</Button>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setDialogAberto(false)} disabled={salvando}>Cancelar</Button><Button variant="contained" onClick={salvar} disabled={salvando}>{salvando ? "Salvando..." : "Salvar"}</Button></DialogActions>
      </Dialog>
      <Snackbar open={notificacao.aberta} autoHideDuration={5000} onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}><Alert severity={notificacao.tipo} onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}>{notificacao.mensagem}</Alert></Snackbar>
    </>
  );
}
